import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plane, MapPin, Calendar } from "lucide-react";
import { API } from "@/App";

const formatPrice = (price, currency = "USD") => {
  if (!price || price === 0) return "Free";
  const symbol = currency === "GBP" ? "£" : currency === "EUR" ? "€" : "$";
  return `${symbol}${Math.round(price)}`;
};

const formatRange = (startIso, endIso) => {
  try {
    const s = new Date(startIso);
    const e = new Date(endIso);
    const opts = { day: "numeric", month: "short" };
    if (s.getMonth() === e.getMonth()) {
      return `${s.toLocaleDateString("en-GB", { day: "numeric" })}–${e.toLocaleDateString("en-GB", opts)}`;
    }
    return `${s.toLocaleDateString("en-GB", opts)} – ${e.toLocaleDateString("en-GB", opts)}`;
  } catch (_) {
    return "";
  }
};

/**
 * Horizontally-scrolling row of trips/retreats. Magazine-style, bigger than series cards.
 * Auto-hides if no trips match the filters.
 */
const TripsRow = ({ city, sport }) => {
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        // Trips have their own destination, not city — so we don't pass city filter here.
        // (Trips should appear regardless of the user's "where I want to play" filter.)
        const params = new URLSearchParams();
        if (sport && sport !== "all") params.append("sport", sport);
        const res = await fetch(`${API}/trips?${params.toString()}`);
        if (res.ok && !cancelled) setTrips(await res.json());
      } catch (e) {
        if (!cancelled) setTrips([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
  }, [sport]);

  if (loading || trips.length === 0) return null;

  return (
    <section className="container mx-auto px-4 pt-2 pb-4" data-testid="trips-row">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Plane className="w-4 h-4 text-primary" />
          <h2 className="font-display text-xl sm:text-2xl">Trips & retreats</h2>
        </div>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-2 -mx-4 px-4 snap-x snap-mandatory">
        {trips.map(t => (
          <button
            key={t.trip_id}
            type="button"
            onClick={() => navigate(`/trips/${t.trip_id}`)}
            className="group relative shrink-0 w-80 sm:w-96 snap-start text-left rounded-2xl overflow-hidden border border-border bg-card hover:border-primary/50 transition-colors"
            data-testid={`trip-card-${t.trip_id}`}
          >
            <div className="relative h-48">
              <img src={t.image_url} alt={t.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
              <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/95 text-primary-foreground text-[10px] font-medium uppercase tracking-wider">
                <Plane className="w-3 h-3" /> Trip
              </span>
              <div className="absolute bottom-3 left-3 right-3 text-white">
                <p className="font-display text-xl leading-tight line-clamp-2">{t.title}</p>
                <p className="text-xs mt-1 flex items-center gap-1.5 opacity-90">
                  <MapPin className="w-3 h-3" /> {t.destination_city}, {t.destination_country}
                </p>
              </div>
            </div>
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Calendar className="w-3.5 h-3.5" />
                {formatRange(t.start_date, t.end_date)}
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">From</p>
                <p className="font-display text-lg">{formatPrice(t.price, t.currency)}</p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
};

export default TripsRow;
